/**
 * OrgWhatsAppPanel — the organisation's WhatsApp number (Meta Cloud API).
 *
 * Customers (e.g. B2B garages) message this number; Cata turns their
 * messages into orders and replies on the same chat. Members see the status;
 * Cata platform admins (status.can_manage) can connect a number by its Meta
 * phone_number_id, or disconnect it.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle, Link2, Loader2, MessageCircle, Trash2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { orgWhatsAppService, OrgWhatsAppStatus } from '../../../services/OrgWhatsAppService';

export const OrgWhatsAppPanel: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<OrgWhatsAppStatus | null>(null);
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      const resp = await orgWhatsAppService.getStatus();
      setStatus(resp.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? t('settings.orgWhatsApp.errors.status', 'Could not load WhatsApp status'));
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const orgId = status?.organization_id ?? null;
  const isConnected = status?.status === 'CONNECTED';
  const canManage = !!status?.can_manage && orgId != null;

  const handleConnect = async () => {
    if (!orgId || !phoneNumberId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await orgWhatsAppService.connect(orgId, {
        phone_number_id: phoneNumberId.trim(),
        waba_id: wabaId.trim() || undefined,
      });
      setStatus(resp.data);
      setPhoneNumberId('');
      setWabaId('');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? t('settings.orgWhatsApp.errors.failedConnect', 'Failed to connect the number'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!orgId) return;
    if (!window.confirm(t('settings.orgWhatsApp.confirmDisconnect', 'Disconnect this WhatsApp number from the organisation?'))) return;
    setLoading(true);
    setError(null);
    try {
      await orgWhatsAppService.disconnect(orgId);
      await fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? t('settings.orgWhatsApp.errors.failedDisconnect', 'Failed to disconnect'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{t('settings.orgWhatsApp.title', 'WhatsApp')}</h3>
          <p className="text-sm text-gray-500">
            {t('settings.orgWhatsApp.subtitle', 'Customers send parts requests to this number — Cata opens the order and replies on the chat')}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 flex items-center gap-2">
          <XCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {!status && !error && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('common.loading', 'Loading…')}
        </div>
      )}

      {status && isConnected && (
        <div className="mb-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 shrink-0 mt-0.5 text-green-600" />
          <div>
            <p className="font-medium">{t('settings.orgWhatsApp.connected', 'Connected')}</p>
            {status.phone_number && (
              <p className="text-green-700 mt-0.5">
                <span className="font-semibold" dir="ltr">+{status.phone_number}</span>
                {status.display_name && ` · ${status.display_name}`}
              </p>
            )}
            {status.connected_at && (
              <p className="text-green-600 text-xs mt-0.5">
                {t('settings.orgWhatsApp.since', 'Since')} {new Date(status.connected_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}

      {status && !isConnected && !canManage && (
        <p className="text-sm text-gray-500">
          {t('settings.orgWhatsApp.notConnected', 'No WhatsApp number is connected yet. Contact the Cata team to get one.')}
        </p>
      )}

      {canManage && !isConnected && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            {t(
              'settings.orgWhatsApp.adminHint',
              'Cata admin: add the number to the WhatsApp Business Account in Meta, then paste its Phone number ID (WhatsApp Manager → API Setup).',
            )}
          </p>
          <input
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value)}
            placeholder={t('settings.orgWhatsApp.phoneNumberId', 'Phone number ID')}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            dir="ltr"
          />
          <input
            value={wabaId}
            onChange={(e) => setWabaId(e.target.value)}
            placeholder={t('settings.orgWhatsApp.wabaId', 'WhatsApp Business Account ID (optional)')}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            dir="ltr"
          />
          <button
            onClick={handleConnect}
            disabled={loading || !phoneNumberId.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {t('settings.orgWhatsApp.connectButton', 'Connect number')}
          </button>
        </div>
      )}

      {canManage && isConnected && (
        <button
          onClick={handleDisconnect}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-600 border border-red-200 text-sm font-medium hover:bg-red-100 disabled:opacity-60 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          {t('settings.orgWhatsApp.disconnectButton', 'Disconnect')}
        </button>
      )}
    </div>
  );
};

export default OrgWhatsAppPanel;
